import { SectionCard } from '@atlas/ui-components'
import { Input } from '@atlas/ui-primitives'
import { X } from 'lucide-react'
import type { WorkOrderLink } from '@/domain/work-order.types'
import { useRowKeys } from '@/hooks/useRowKeys'
import type { WorkOrderFormState } from './form-model'

type Set = <K extends keyof WorkOrderFormState>(key: K, value: WorkOrderFormState[K]) => void

/** Related links — named (name + url) rows, add/remove. */
export function LinksCard({ form, set }: { form: WorkOrderFormState; set: Set }) {
	const update = (i: number, patch: Partial<WorkOrderLink>) =>
		set(
			'links',
			form.links.map((l, idx) => (idx === i ? { ...l, ...patch } : l)),
		)
	const rowKeys = useRowKeys(form.links.length)
	const add = () => {
		rowKeys.add()
		set('links', [...form.links, { name: '', url: '' }])
	}
	const remove = (i: number) => {
		rowKeys.remove(i)
		set(
			'links',
			form.links.filter((_, idx) => idx !== i),
		)
	}
	const rows = form.links.map((link, i) => ({ link, key: rowKeys.keys[i] }))
	return (
		<SectionCard title="Related links" className="p-6">
			<div className="space-y-3">
				{rows.map(({ link, key }, i) => (
					<div key={key} className="flex items-center gap-2">
						<Input
							aria-label={`Link ${i + 1} name`}
							value={link.name}
							onChange={(e) => update(i, { name: e.target.value })}
							placeholder="Name (e.g. Veeva)"
							className="w-1/3"
						/>
						<Input
							aria-label={`Link ${i + 1} URL`}
							value={link.url}
							onChange={(e) => update(i, { url: e.target.value })}
							placeholder="https://…"
							className="flex-1"
						/>
						<button
							type="button"
							aria-label={`Remove link ${i + 1}`}
							onClick={() => remove(i)}
							className="text-ds-text-subtle hover:text-ds-danger-strong"
						>
							<X className="h-4 w-4" />
						</button>
					</div>
				))}
				<button type="button" onClick={add} className="text-ds-sm font-medium text-ds-primary hover:underline">
					+ Add link
				</button>
			</div>
		</SectionCard>
	)
}
